import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Announcements.css';

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get('/api/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort announcements
  const processedAnnouncements = announcements
    .filter(announcement => {
      const matchesSearch = announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          announcement.content.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || announcement.type === filterType;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.date) - new Date(a.date);
      } else {
        return new Date(a.date) - new Date(b.date);
      }
    });

  const getAnnouncementType = (announcement) => {
    // Determine announcement type based on content or default to 'General'
    if (announcement.type) return announcement.type;
    if (announcement.title.toLowerCase().includes('call')) return 'Call for Papers';
    if (announcement.title.toLowerCase().includes('deadline')) return 'Deadline';
    if (announcement.title.toLowerCase().includes('event')) return 'Event';
    return 'General';
  };

  const getPriorityClass = (announcement) => {
    if (announcement.priority === 'high') return 'priority-high';
    if (announcement.priority === 'low') return 'priority-low';
    return 'priority-normal';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  return (
    <div className="announcements-page">
      <div className="announcements-hero">
        <div className="container">
          <div className="announcements-hero-content">
            <div className="announcements-icon">
              <i className="fas fa-bullhorn"></i>
            </div>
            <h1 className="announcements-title">Announcements</h1>
            <p className="announcements-subtitle">
              Stay updated with the latest news, calls for papers, and important deadlines
            </p>
          </div>
        </div>
      </div>

      <div className="container announcements-container">
        <div className="announcements-filters">
          <div className="filter-row">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search announcements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-dropdown">
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                <option value="all">All Types</option>
                <option value="Call for Papers">Call for Papers</option>
                <option value="Deadline">Deadlines</option>
                <option value="Event">Events</option>
                <option value="General">General</option>
              </select>
            </div>
            <div className="filter-dropdown">
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="announcements-loading">
            <div className="loading-spinner"></div>
            <p>Loading announcements...</p>
          </div>
        ) : processedAnnouncements.length > 0 ? (
          processedAnnouncements.map((announcement, index) => (
            <div
              key={announcement.id}
              className="announcement-card"
              style={{ '--announcement-index': index }}
            >
              <div className="announcement-header">
                <span className="announcement-type">
                  {getAnnouncementType(announcement)}
                </span>
                <h2 className="announcement-title">{announcement.title}</h2>
                <div className="announcement-meta">
                  <span>
                    <i className="fas fa-calendar"></i>
                    {formatDate(announcement.date)}
                  </span>
                  {announcement.author && (
                    <span>
                      <i className="fas fa-user"></i>
                      {announcement.author}
                    </span>
                  )}
                  {announcement.category && (
                    <span>
                      <i className="fas fa-folder"></i>
                      {announcement.category}
                    </span>
                  )}
                </div>
              </div>

              <div className="announcement-body">
                <div className="announcement-content">
                  {announcement.content}
                </div>

                {announcement.attachments && announcement.attachments.length > 0 && (
                  <div className="announcement-attachments">
                    <p className="attachment-title">Attachments:</p>
                    <div className="attachment-list">
                      {announcement.attachments.map((attachment, idx) => (
                        <a
                          key={idx}
                          href={attachment.url}
                          className="attachment-item"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fas fa-file-download"></i>
                          {attachment.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {(announcement.tags || announcement.priority) && (
                <div className="announcement-footer">
                  {announcement.tags && (
                    <div className="announcement-tags">
                      {announcement.tags.map((tag, idx) => (
                        <span key={idx} className="announcement-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  {announcement.priority && (
                    <div className="announcement-priority">
                      <span className={`priority-badge ${getPriorityClass(announcement)}`}>
                        <i className="fas fa-flag"></i>
                        {announcement.priority.charAt(0).toUpperCase() + announcement.priority.slice(1)} Priority
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="no-announcements">
            <div className="no-announcements-icon">
              <i className="fas fa-inbox"></i>
            </div>
            <p className="no-announcements-text">
              {searchTerm || filterType !== 'all'
                ? 'No announcements found matching your criteria.'
                : 'No announcements at this time. Please check back later.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Announcements;